import { useState, type ReactNode } from 'react';
import { useTranslation } from '@/lib/i18n';
import { Button, Input, RadioGroup, Select } from '@/components/ui';
import type { ClaimReason, ClaimStatus, ClaimType } from '@/lib/api-admin/types';
import {
  CLAIM_SEARCH_TYPES,
  useClaimFiltersStore,
  type ClaimFilterInputs,
  type ClaimSearchType,
} from '@/stores/useClaimFiltersStore';
import {
  CLAIM_LIST_STATUSES,
  CLAIM_LIST_STATUS_LABELS,
  CLAIM_LIST_TYPES,
  CLAIM_LIST_TYPE_LABELS,
} from '../lib/claimListLabels';

const CLAIM_REASON_OPTIONS: ReadonlyArray<ClaimReason> = [
  'DEFECTIVE',
  'WRONG_DELIVERY',
  'MIND_CHANGE',
  'SHIPPING_DELAY',
  'OTHER',
];

const SEARCH_TYPE_LABELS: Record<ClaimSearchType, string> = {
  ALL: '통합검색',
  PRODUCT_NAME: '상품명',
  PRODUCT_CODE: '상품코드',
  GOODS_CD: '자체상품코드',
  ORDER_NO: '주문번호',
  ORDERER_NAME: '주문자명',
};

const EMPTY: ClaimFilterInputs = {
  searchType: 'ALL',
  q: '',
  claimStatus: null,
  claimType: null,
  reasonCode: null,
  dateFrom: null,
  dateTo: null,
  orderDateFrom: null,
  orderDateTo: null,
};

/**
 * QA-626 클레임 검색 — 주문 관리처럼 [검색]을 눌러야 조회한다(입력마다 조회하지 않는다).
 * 검색어 유형 select + 입력(placeholder 없음), 유형·상태·사유는 라디오, 주문일·클레임일 기간.
 */
export const ClaimFiltersBar = () => {
  const { t } = useTranslation('claims');
  const applyFilters = useClaimFiltersStore((s) => s.applyFilters);
  const reset = useClaimFiltersStore((s) => s.reset);
  const [draft, setDraft] = useState<ClaimFilterInputs>(() => {
    const s = useClaimFiltersStore.getState();
    return {
      searchType: s.searchType,
      q: s.q,
      claimStatus: s.claimStatus,
      claimType: s.claimType,
      reasonCode: s.reasonCode,
      dateFrom: s.dateFrom,
      dateTo: s.dateTo,
      orderDateFrom: s.orderDateFrom,
      orderDateTo: s.orderDateTo,
    };
  });
  const patch = (p: Partial<ClaimFilterInputs>) => setDraft((d) => ({ ...d, ...p }));

  const allLabel = t('claims.filters.all', { defaultValue: '전체' });
  const radio = <T extends string>(
    values: ReadonlyArray<T>,
    labelOf: (v: T) => string,
    value: T | null,
    onChange: (v: T | null) => void,
    ariaLabel: string,
    testId: string,
  ) => (
    <RadioGroup
      orientation="horizontal"
      value={value ?? ''}
      onValueChange={(v) => onChange(v === '' ? null : (v as T))}
      options={[{ value: '', label: allLabel }, ...values.map((v) => ({ value: v, label: labelOf(v) }))]}
      aria-label={ariaLabel}
      className="flex-wrap gap-x-4 gap-y-1"
      data-testid={testId}
    />
  );

  const row = (label: ReactNode, children: ReactNode, htmlFor?: string) => (
    <>
      <span className="py-1.5 font-semibold" style={{ color: 'var(--content-strong)' }}>
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
      </span>
      <div className="min-w-0">{children}</div>
    </>
  );

  const dateRange = (
    from: string | null,
    to: string | null,
    onFrom: (v: string | null) => void,
    onTo: (v: string | null) => void,
    idPrefix: string,
    label: string,
  ) => (
    <div className="flex items-center gap-2">
      <Input
        id={`${idPrefix}-from`}
        type="date"
        value={from ?? ''}
        max={to ?? undefined}
        onChange={(e) => onFrom(e.target.value === '' ? null : e.target.value)}
        aria-label={`${label} 시작일`}
        className="w-40"
      />
      <span aria-hidden="true">~</span>
      <Input
        id={`${idPrefix}-to`}
        type="date"
        value={to ?? ''}
        min={from ?? undefined}
        onChange={(e) => onTo(e.target.value === '' ? null : e.target.value)}
        aria-label={`${label} 종료일`}
        className="w-40"
      />
    </div>
  );

  return (
    <form
      aria-label={t('claims.filters.title')}
      className="flex flex-col gap-3 rounded-lg p-4 text-[13px]"
      style={{ background: 'var(--surface-raised)', border: '1px solid var(--divider-strong)' }}
      onSubmit={(e) => {
        e.preventDefault();
        applyFilters({ ...draft, q: draft.q.trim() });
      }}
      data-testid="claims-filters"
    >
      <div className="grid grid-cols-[96px_minmax(0,1fr)] items-start gap-x-3 gap-y-2">
        {row(
          t('claims.filters.q'),
          <div className="flex max-w-[560px] gap-2">
            <Select
              className="w-36 shrink-0"
              value={draft.searchType}
              onChange={(e) => patch({ searchType: e.target.value as ClaimSearchType })}
              aria-label="검색어 유형"
              data-testid="claim-search-type"
            >
              {CLAIM_SEARCH_TYPES.map((st) => (
                <option key={st} value={st}>
                  {SEARCH_TYPE_LABELS[st]}
                </option>
              ))}
            </Select>
            <Input
              id="claim-q"
              value={draft.q}
              onChange={(e) => patch({ q: e.target.value })}
              className="min-w-0 flex-1"
              maxLength={100}
              data-testid="claim-q"
            />
          </div>,
          'claim-q',
        )}
        {row(
          t('claims.filters.claimType'),
          radio<ClaimType>(CLAIM_LIST_TYPES, (v) => CLAIM_LIST_TYPE_LABELS[v] ?? v, draft.claimType, (v) => patch({ claimType: v }), t('claims.filters.claimType'), 'claim-type'),
        )}
        {row(
          t('claims.filters.claimStatus'),
          radio<ClaimStatus>(CLAIM_LIST_STATUSES, (v) => CLAIM_LIST_STATUS_LABELS[v] ?? v, draft.claimStatus, (v) => patch({ claimStatus: v }), t('claims.filters.claimStatus'), 'claim-status'),
        )}
        {row(
          t('claims.filters.reasonCode'),
          radio<ClaimReason>(CLAIM_REASON_OPTIONS, (v) => t(`claims.reasonCode.${v}`), draft.reasonCode, (v) => patch({ reasonCode: v }), t('claims.filters.reasonCode'), 'claim-reason'),
        )}
        {row(
          '주문일시',
          dateRange(draft.orderDateFrom, draft.orderDateTo, (v) => patch({ orderDateFrom: v }), (v) => patch({ orderDateTo: v }), 'claim-order-date', '주문일'),
        )}
        {row(
          '클레임일시',
          dateRange(draft.dateFrom, draft.dateTo, (v) => patch({ dateFrom: v }), (v) => patch({ dateTo: v }), 'claim-date', '클레임일'),
        )}
      </div>
      <div className="flex justify-center gap-2">
        <Button type="submit" variant="primary" size="sm" data-testid="claim-search">
          {t('claims.actions.search', { defaultValue: '검색' })}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setDraft(EMPTY);
            reset();
          }}
          data-testid="claim-reset"
        >
          {t('claims.actions.resetFilters')}
        </Button>
      </div>
    </form>
  );
};
